/**
 * Personalizing the demo to the visitor's client (SKE-4166, phase 4) — client
 * half.
 *
 * An unlocked visitor names the client they actually work with, and the board
 * re-skins: their client's name in the approver header, their logo, their
 * colour. The "this could be you" moment the demo exists to produce.
 *
 * ------------------------------ NEVER BROKEN --------------------------------
 * Personalization has exactly one failure mode we accept — "we didn't find a
 * logo" — and none we don't. There is no error state, no empty state and no
 * dead end, because every field this module produces has a deterministic local
 * fallback:
 *
 *   name    always derivable from what the visitor typed;
 *   accent  a hash of the domain into a fixed, curated palette;
 *   logo    a monogram rendered locally as an SVG data URI.
 *
 * So a rate-limited endpoint, an offline visitor, a brand nobody has ever
 * indexed and a server that isn't configured at all all produce the same thing:
 * a board with their client's name on it. The server call only makes it richer.
 * `source` records which happened so the funnel can be measured honestly rather
 * than flattered.
 *
 * ------------------------------- WHY A SERVER -------------------------------
 * The patch itself is client-side, like every other demo mutation (see the hard
 * safety invariant at the top of demoApi.ts). The endpoint exists only because
 * the brand API needs a secret, and because a domain a stranger typed should be
 * validated somewhere that isn't a marketing page. Critically, the server does
 * NOT fetch the visitor's URL — see services/demoBrandDomain.ts on the server.
 */

/** The applied personalization. Persisted in the demo store; cleared by reset. */
export interface DemoBrand {
  /** Display name — the client's brand as it appears in the UI. */
  name: string
  /** Registrable domain or bare handle, kept for analytics and re-derivation. */
  domain: string
  /** Absolute https URL, or a locally-generated monogram data URI. Never empty. */
  logoUrl: string
  /** `#rrggbb`. Always present — derived when the brand API had no colour. */
  accent: string
  /** Where the identity came from, for honest funnel reporting. */
  source: 'brandfetch' | 'derived'
  appliedAt: string
}

/** Path on schedugram-server. Kept in one place; the server mirrors it. */
export const DEMO_BRAND_PATH = '/public/demo-brand'

/**
 * Reuses `VITE_DEMO_LEAD_API_URL` deliberately: both endpoints live on the same
 * schedugram-server, and a second variable naming the same origin is one more
 * thing to get wrong in four `.env` files. If they ever diverge, split it then.
 *
 * Unlike the lead gate — which hides itself entirely when unconfigured, because
 * a form that can only fail would corrupt the unlock KPI — an unset base URL
 * here is survivable: `null` just means the lookup is skipped and the brand is
 * derived locally. There is nothing to hide and no denominator to protect.
 */
export const getDemoBrandEndpoint = (): string | null => {
  const base = import.meta.env.VITE_DEMO_LEAD_API_URL?.trim()

  if (!base) {
    return null
  }

  return `${base.replace(/\/+$/, '')}${DEMO_BRAND_PATH}`
}

const MAX_NAME_LENGTH = 40
const MAX_INPUT_LENGTH = 253

/**
 * Is this worth sending? A UX affordance so an obvious typo doesn't cost a
 * round-trip — NOT validation. The server is the authority and re-derives
 * everything from scratch; nothing here is a security control.
 */
export const isLikelyBrandInput = (value: string): boolean => {
  const input = value.trim()

  if (!input || input.length > MAX_INPUT_LENGTH) {
    return false
  }

  // An @handle needs no dot; anything else has to look at least domain-shaped.
  return input.startsWith('@') ? input.length > 1 : /\.[a-z]{2,}/i.test(input)
}

/**
 * Reduce whatever the visitor typed to a seed for the local fallbacks.
 *
 * A deliberately loose mirror of the server's normaliser: it only has to be
 * stable and recognisable, because the only things derived from it are a
 * display name and a colour. The server's version is the one doing safety work.
 */
export const toBrandSeed = (value: string): string => {
  const input = value.trim().toLowerCase()

  if (input.startsWith('@')) {
    return input.slice(1)
  }

  return input
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/^www\./, '')
    .split(/[/?#]/)[0]
}

/** `marigold-and-rye.co.uk` -> `Marigold And Rye`. */
export const deriveBrandName = (value: string): string => {
  const [head] = toBrandSeed(value).split('.')

  const name = head
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) =>
      /^[a-z]/.test(word) ? word[0].toUpperCase() + word.slice(1) : word
    )
    .join(' ')
    .trim()
    .slice(0, MAX_NAME_LENGTH)

  return name || toBrandSeed(value).slice(0, MAX_NAME_LENGTH) || 'Your client'
}

/**
 * The accent palette used when the brand API gave us no colour.
 *
 * Hand-picked rather than generated: a free hue rotation reliably produces
 * something garish for one visitor in six, and the whole point of this moment
 * is that their board looks *considered*. Every entry is mid-dark, so white
 * monogram text on it clears contrast, and none of them collide with the demo's
 * own blue chrome or with the status colours (green / amber / red), which have
 * to keep meaning what they mean.
 */
export const DEMO_BRAND_PALETTE = [
  '#2f6f5e', // pine
  '#8c3b4a', // wine
  '#3b5a8c', // slate blue
  '#a35c2a', // amber brown
  '#5b4a8c', // plum
  '#2b6a7a', // petrol
  '#7a5c1f', // ochre
  '#6b3a6b', // mulberry
] as const

/**
 * Deterministic accent for a seed — FNV-1a, so the same client always gets the
 * same colour. That matters more than it sounds: a visitor who resets the demo
 * and re-personalizes must not watch "their" brand colour change, which would
 * read as arbitrary rather than derived.
 */
export const deriveBrandAccent = (seed: string): string => {
  let hash = 0x811c9dc5

  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    // FNV prime, via shifts — `Math.imul` keeps it in 32-bit range.
    hash = Math.imul(hash, 0x01000193) >>> 0
  }

  return DEMO_BRAND_PALETTE[hash % DEMO_BRAND_PALETTE.length]
}

/** Exactly `#rrggbb`, lowercased. Anything else is dropped. */
export const sanitiseAccent = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }
  const hex = value.trim().toLowerCase()
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : undefined
}

/**
 * Only an absolute https URL survives.
 *
 * This value ends up in an `<img src>`, so `javascript:`, `data:` and protocol-
 * relative URLs are dropped rather than escaped — the monogram fallback below
 * looks deliberate, so there is never a reason to accept a questionable one.
 * The server applies the same rule; this is the second of the two checks,
 * because the server is not the only thing that could ever call this.
 */
export const sanitiseLogoUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.length > 2000) {
    return undefined
  }

  try {
    const url = new URL(value)
    return url.protocol === 'https:' ? url.toString() : undefined
  } catch {
    return undefined
  }
}

/**
 * A monogram logo, generated locally as an SVG data URI.
 *
 * This is what makes "we found no logo" indistinguishable from success at a
 * glance: a rounded tile in the brand's accent with its initial, which is what
 * a real product would show for an account with no avatar.
 *
 * Injection surface is zero by construction rather than by escaping: exactly
 * one character is emitted, drawn from `[A-Z0-9]`, and the fill is a
 * `#rrggbb` string that has already been through `sanitiseAccent`. Anything
 * else becomes the bullet-proof default. (An SVG in an `<img src>` cannot run
 * script regardless — this is belt and braces.)
 */
export const buildMonogramLogo = (name: string, accent: string): string => {
  const initial = (name.match(/[a-z0-9]/i)?.[0] ?? 'C').toUpperCase()
  const fill = sanitiseAccent(accent) ?? DEMO_BRAND_PALETTE[0]

  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">`,
    `<rect width="64" height="64" rx="14" fill="${fill}"/>`,
    `<text x="32" y="33" fill="#ffffff" font-family="Helvetica,Arial,sans-serif"`,
    ` font-size="32" font-weight="700" text-anchor="middle"`,
    ` dominant-baseline="central">${initial}</text>`,
    `</svg>`,
  ].join('')

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/* -------------------------------------------------------------------------- */
/* Server response -> applied brand                                            */
/* -------------------------------------------------------------------------- */

/** The tokens the endpoint returns. Every field is optional to this parser. */
export interface DemoBrandTokens {
  name?: unknown
  domain?: unknown
  logoUrl?: unknown
  accent?: unknown
  source?: unknown
}

/**
 * Complete a partial (or absent) server response into a fully-applied brand.
 *
 * Every gap is filled locally, which is what guarantees this function cannot
 * return something the UI has to apologise for. Pure and synchronous, so the
 * offline path and the happy path run the same code.
 *
 * @param input  what the visitor typed — the seed for every fallback
 * @param tokens the server's answer, or null when the call never landed
 */
export const resolveDemoBrand = (
  input: string,
  tokens: DemoBrandTokens | null,
  now: Date = new Date()
): DemoBrand => {
  const seed = toBrandSeed(input)

  const domain =
    (typeof tokens?.domain === 'string' && tokens.domain.trim()) || seed

  const name =
    (typeof tokens?.name === 'string' &&
      tokens.name.trim().slice(0, MAX_NAME_LENGTH)) ||
    deriveBrandName(input)

  // The brand's own colour when we have one, otherwise a stable colour for this
  // domain. Keyed on the domain rather than the raw input so `acme.com` and
  // `https://www.acme.com/about` land on the same accent.
  const accent = sanitiseAccent(tokens?.accent) ?? deriveBrandAccent(domain)

  const logoUrl =
    sanitiseLogoUrl(tokens?.logoUrl) ?? buildMonogramLogo(name, accent)

  return {
    name,
    domain,
    logoUrl,
    accent,
    // Only claim `brandfetch` when the server did AND something usable survived
    // sanitisation — otherwise the analytics would over-report real matches.
    source:
      tokens?.source === 'brandfetch' && sanitiseLogoUrl(tokens?.logoUrl)
        ? 'brandfetch'
        : 'derived',
    appliedAt: now.toISOString(),
  }
}

/* -------------------------------------------------------------------------- */
/* The request                                                                 */
/* -------------------------------------------------------------------------- */

export type DemoBrandFailureReason =
  | 'not-configured'
  | 'invalid-domain'
  | 'blocked-domain'
  | 'rate-limited'
  | 'network'
  | 'server'

export type DemoBrandResult =
  | { ok: true; tokens: DemoBrandTokens }
  | { ok: false; reason: DemoBrandFailureReason }

const failureForStatus = (status: number): DemoBrandFailureReason => {
  if (status === 429) return 'rate-limited'
  if (status === 400 || status === 422) return 'invalid-domain'
  return 'server'
}

/**
 * Ask the server for brand tokens.
 *
 * Anonymous, no credentials — same posture as the lead POST, and for the same
 * reasons (see demoLead.ts). A failure here is not a failure of the feature:
 * every caller pairs this with `resolveDemoBrand`, which personalizes anyway.
 */
export const fetchDemoBrand = async (
  website: string,
  sessionId?: string
): Promise<DemoBrandResult> => {
  const endpoint = getDemoBrandEndpoint()

  if (!endpoint) {
    return { ok: false, reason: 'not-configured' }
  }

  let response: Response
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        website: website.trim().slice(0, MAX_INPUT_LENGTH),
        ...(sessionId ? { sessionId } : {}),
      }),
    })
  } catch {
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) {
    let reason = failureForStatus(response.status)
    try {
      const body = (await response.json()) as { code?: string }
      // `blocked-domain` is the one failure worth its own copy — the visitor
      // typed something real, we just won't skin the demo as it.
      if (body?.code === 'blocked-domain') {
        reason = 'blocked-domain'
      } else if (body?.code === 'invalid-domain') {
        reason = 'invalid-domain'
      }
    } catch {
      // Non-JSON body — the status-derived reason stands.
    }
    return { ok: false, reason }
  }

  try {
    const body = (await response.json()) as { brand?: DemoBrandTokens }
    return { ok: true, tokens: body?.brand ?? {} }
  } catch {
    return { ok: false, reason: 'server' }
  }
}
